import { ory } from 'src/auth/ory';
import { isOryApiError } from 'src/auth/utils/ory-helpers';
import { Logger } from 'src/utils/Logger';

export const RETURN_TO_PARAM = 'return_to';

const PATHS_NEVER_RETURNED_TO = ['/login', '/logout'];
const RECENT_RETURN_KEY = 'login_recent_return';
const RETURN_LOOP_WINDOW_MS = 10_000;

// Resolving against our own origin and comparing origins rejects absolute URLs, protocol-relative
// `//host` and the `/\host` form browsers treat the same way. A URL on our origin can still have
// a pathname starting with `//` (`/.//host`, `https://<us>//host`), and navigating to that
// pathname on its own leaves the site, so it is rejected too
export function toSafeReturnPath(value: string | null | undefined): string | null {
  if (!value) return null;

  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;
  if (url.pathname.startsWith('//')) return null;
  if (
    PATHS_NEVER_RETURNED_TO.some(
      path => url.pathname === path || url.pathname.startsWith(`${path}/`),
    )
  ) {
    return null;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

// The API answers 401 when a third-party call it makes is rejected, even though the user's own
// session is fine. A page that does that on load sends the user back to login, and returning
// them to it again would bounce between the two forever, so a quick second return goes home
export function returnPathWithoutLooping(returnPath: string): string {
  try {
    const recent = JSON.parse(sessionStorage.getItem(RECENT_RETURN_KEY) ?? 'null') as {
      path: string;
      at: number;
    } | null;
    if (recent?.path === returnPath && Date.now() - recent.at < RETURN_LOOP_WINDOW_MS) {
      sessionStorage.removeItem(RECENT_RETURN_KEY);
      return '/';
    }
    sessionStorage.setItem(RECENT_RETURN_KEY, JSON.stringify({ path: returnPath, at: Date.now() }));
  } catch (e) {
    Logger.error('sessionStorage unavailable for login return loop guard', e);
  }
  return returnPath;
}

export function loginPathReturningTo(returnPath: string): string {
  const safeReturnPath = toSafeReturnPath(returnPath);
  if (!safeReturnPath || safeReturnPath === '/') return '/login';
  return `/login?${new URLSearchParams({ [RETURN_TO_PARAM]: safeReturnPath })}`;
}

// Ory redirects to the flow's return_to after social sign-in, and rejects one outside the
// project's allowed return URLs. Landing on the home page beats failing to show the login form
export async function createLoginFlowReturningTo(returnPath: string | null) {
  if (!returnPath) return ory.createBrowserLoginFlow();

  try {
    return await ory.createBrowserLoginFlow({
      returnTo: `${window.location.origin}${returnPath}`,
    });
  } catch (e) {
    if (isOryApiError(e) && e.response?.status === 400) {
      Logger.warn('Ory rejected the login return URL, logging in without it', { returnPath });
      return ory.createBrowserLoginFlow();
    }
    throw e;
  }
}
